import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import "./SignIn.css";
import { Link } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Card,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import PasswordIcon from "@mui/icons-material/Password";
import LockIcon from "@mui/icons-material/Lock";

import { Field, Form, Formik } from "formik";

import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { ResetLink, requestPasswordOtp, resetPasswordWithOtp } from "./Apihelper/Profile";
import AppToast from '../components/Toast/AppToast';

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
});

const fieldSx = {
  marginBottom: "16px",
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: "#1B4332",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1B4332",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#1B4332",
  },
};

const submitButtonSx = {
  background: "linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)",
  color: "white",
  padding: "12px",
  borderRadius: "8px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(26, 35, 126, 0.2)",
  "&:hover": {
    background: "linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)",
    boxShadow: "0 6px 16px rgba(26, 35, 126, 0.3)",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.8)",
    background: "linear-gradient(135deg, #52796F 0%, #84A98C 100%)",
  },
};

const textButtonSx = {
  color: "#1B4332",
  fontWeight: 600,
  textTransform: "none",
  padding: "4px 8px",
  minWidth: 0,
  "&.Mui-disabled": { color: "#94a3b8" },
};

const RESEND_DEFAULT_WAIT = 60;

const describeContacts = (contacts) => {
  const parts = [];
  if (contacts && contacts.phone) parts.push(`WhatsApp ${contacts.phone}`);
  if (contacts && contacts.email) parts.push(contacts.email);
  return parts.join(" and ");
};

const ForgetPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // "link" = existing reset-by-email-link flow, "otp" = code via WhatsApp / email
  const [mode, setMode] = useState("link");
  const [otpStage, setOtpStage] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [contacts, setContacts] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [resendWait, setResendWait] = useState(0);
  const [resetDone, setResetDone] = useState(false);
  const redirectTimer = useRef(null);

  useEffect(() => {
    if (resendWait <= 0) return undefined;
    const timer = setTimeout(() => setResendWait((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearTimeout(timer);
  }, [resendWait]);

  useEffect(() => () => {
    if (redirectTimer.current) clearTimeout(redirectTimer.current);
  }, []);

  const showToast = (message, severity) => {
    setSnackbarMessage(message || "An error occurred. Please try again.");
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleModeChange = (event, value) => {
    if (!value || loading) return;
    setMode(value);
    setOtpError("");
  };

  const initialValues = {
    email: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(false);
    setLoading(true);

    try {
      const redirectUrl = window.location.origin + '/reset-password';
      const res = await ResetLink(values.email, redirectUrl);
      if (res.code === "200" || res.code === 200) {
        setSnackbarMessage("Reset password link sent to your email");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        navigate("/", { state: { showSnackbar: true } });
      } else {
        setSnackbarMessage(res.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // ---- Code via WhatsApp / email ----

  const sendCode = async () => {
    const id = identifier.trim();
    if (!id) {
      setOtpError("Please enter your email, username or phone number.");
      return;
    }
    if (loading || resendWait > 0) return;
    setLoading(true);
    setOtpError("");
    try {
      const res = await requestPasswordOtp(id);
      const code = String(res.code);
      if (code === "200") {
        setContacts(res.contacts || null);
        setOtpInfo(res.message || "If the account exists, a code has been sent.");
        setOtpStage(2);
        setOtpCode("");
        setResendWait(Number(res.wait) > 0 ? Number(res.wait) : RESEND_DEFAULT_WAIT);
        showToast(res.message || "Code sent.", "success");
      } else if (code === "429") {
        setResendWait(Number(res.wait) > 0 ? Number(res.wait) : RESEND_DEFAULT_WAIT);
        setOtpError(res.message || "Please wait before requesting another code.");
        showToast(res.message, "warning");
      } else {
        setOtpError(res.message || "Could not send the code.");
        showToast(res.message, "error");
      }
    } catch (error) {
      showToast("An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = (event) => {
    event.preventDefault();
    sendCode();
  };

  const handleResetWithCode = async (event) => {
    event.preventDefault();
    if (loading || resetDone) return;
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError("Please enter the 6-digit code.");
      return;
    }
    if (newPass.length < 6) {
      setOtpError("Password must be at least 6 characters.");
      return;
    }
    if (newPass !== confirmPass) {
      setOtpError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setOtpError("");
    try {
      const res = await resetPasswordWithOtp(identifier.trim(), otpCode, newPass, confirmPass);
      if (String(res.code) === "200") {
        setResetDone(true);
        setOtpCode("");
        setNewPass("");
        setConfirmPass("");
        showToast(res.message || "Password reset successfully. Please sign in.", "success");
        redirectTimer.current = setTimeout(() => navigate("/"), 2000);
      } else {
        setOtpError(res.message || "Invalid or expired code.");
        showToast(res.message, "error");
      }
    } catch (error) {
      showToast("An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeIdentifier = () => {
    if (loading) return;
    setOtpStage(1);
    setOtpCode("");
    setNewPass("");
    setConfirmPass("");
    setOtpError("");
    setOtpInfo("");
  };

  const contactText = describeContacts(contacts);

  const subtitle =
    mode === "link"
      ? "Enter your email address and we'll send you a link to reset your password"
      : otpStage === 1
        ? "Enter your email, username or phone number and we'll send a 6-digit code to your WhatsApp and email"
        : contactText
          ? `Enter the 6-digit code we sent to ${contactText} and choose a new password`
          : "Enter the 6-digit code you received and choose a new password";

  const backToLogin = (
    <Box sx={{ textAlign: "center", marginTop: "24px" }}>
      <Link
        to="/"
        style={{
          color: "#1B4332",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Back to Login
      </Link>
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        background: "linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)",
        display: "flex",
        justifyContent: "center",
        position: "fixed",
        top: 0,
        left: 0,
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <Container maxWidth="sm" sx={{ my: "auto", py: 2 }}>
        <Card
          elevation={8}
          sx={{
            padding: { xs: "24px 20px", sm: "40px" },
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box textAlign="center" mb={3}>
            <Typography
              variant="h5"
              component="p"
              sx={{
                color: "#1B4332",
                fontWeight: 700,
                marginBottom: "4px",
                fontSize: { xs: "1.2rem", sm: "1.5rem" },
              }}
            >
              Maa Tara Tarini Multi gym
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: "#1B4332",
                fontWeight: 600,
                marginBottom: "8px",
                fontSize: { xs: "1.6rem", sm: "2.125rem" },
              }}
            >
              Forgot Password?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#666",
                maxWidth: "400px",
                margin: "0 auto",
                lineHeight: 1.5,
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={mode}
            exclusive
            fullWidth
            size="small"
            onChange={handleModeChange}
            aria-label="Reset method"
            sx={{
              mb: 3,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                color: "#1B4332",
                borderColor: "rgba(27, 67, 50, 0.3)",
                lineHeight: 1.3,
                py: 1,
              },
              "& .MuiToggleButton-root.Mui-selected": {
                color: "#fff",
                background: "linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)",
              },
              "& .MuiToggleButton-root.Mui-selected:hover": {
                background: "linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)",
              },
            }}
          >
            <ToggleButton value="link" disabled={loading}>Email link</ToggleButton>
            <ToggleButton value="otp" disabled={loading}>Code via WhatsApp / email</ToggleButton>
          </ToggleButtonGroup>

          {mode === "link" && (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched }) => (
                <Form>
                  <Field
                    as={TextField}
                    name="email"
                    fullWidth
                    label="Email Address"
                    variant="outlined"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ ...fieldSx, marginBottom: "24px" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: "#1B4332" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button type="submit" fullWidth disabled={loading} sx={submitButtonSx}>
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>

                  {backToLogin}
                </Form>
              )}
            </Formik>
          )}

          {mode === "otp" && otpStage === 1 && (
            <Box component="form" onSubmit={handleSendCode} noValidate>
              <TextField
                fullWidth
                autoFocus
                label="Email, username or phone"
                variant="outlined"
                value={identifier}
                onChange={(e) => {
                  setIdentifier(e.target.value);
                  if (otpError) setOtpError("");
                }}
                disabled={loading}
                autoComplete="username"
                error={Boolean(otpError)}
                helperText={otpError || " "}
                sx={{ ...fieldSx, marginBottom: "16px" }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: "#1B4332" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading || resendWait > 0}
                sx={submitButtonSx}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : resendWait > 0 ? (
                  `Send code (wait ${resendWait}s)`
                ) : (
                  "Send code"
                )}
              </Button>

              <Box sx={{ textAlign: "center", mt: 1.5 }}>
                <Button
                  type="button"
                  size="small"
                  disabled={loading || !identifier.trim()}
                  onClick={() => {
                    setOtpError("");
                    setOtpStage(2);
                  }}
                  sx={textButtonSx}
                >
                  I already have a code
                </Button>
              </Box>

              {backToLogin}
            </Box>
          )}

          {mode === "otp" && otpStage === 2 && (
            <Box component="form" onSubmit={handleResetWithCode} noValidate>
              {otpInfo && !resetDone && (
                <Typography variant="body2" sx={{ color: "#2D6A4F", textAlign: "center", mb: 2 }}>
                  {otpInfo}
                </Typography>
              )}
              {resetDone && (
                <Typography
                  variant="body1"
                  role="status"
                  sx={{ color: "#1B4332", fontWeight: 600, textAlign: "center", mb: 2 }}
                >
                  Password reset successfully. Redirecting you to login...
                </Typography>
              )}

              <TextField
                fullWidth
                autoFocus
                label="6-digit code"
                variant="outlined"
                value={otpCode}
                onChange={(e) => {
                  setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  if (otpError) setOtpError("");
                }}
                disabled={loading || resetDone}
                sx={fieldSx}
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 6,
                    autoComplete: "one-time-code",
                    style: { letterSpacing: "0.35em" },
                  },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PasswordIcon sx={{ color: "#1B4332" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                fullWidth
                type="password"
                label="New password"
                variant="outlined"
                value={newPass}
                onChange={(e) => {
                  setNewPass(e.target.value);
                  if (otpError) setOtpError("");
                }}
                disabled={loading || resetDone}
                autoComplete="new-password"
                sx={fieldSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#1B4332" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                fullWidth
                type="password"
                label="Confirm new password"
                variant="outlined"
                value={confirmPass}
                onChange={(e) => {
                  setConfirmPass(e.target.value);
                  if (otpError) setOtpError("");
                }}
                disabled={loading || resetDone}
                autoComplete="new-password"
                error={Boolean(otpError)}
                helperText={otpError || " "}
                sx={fieldSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#1B4332" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading || resetDone}
                sx={submitButtonSx}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Reset password"
                )}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1.5,
                }}
              >
                <Button
                  type="button"
                  size="small"
                  onClick={handleChangeIdentifier}
                  disabled={loading || resetDone}
                  sx={textButtonSx}
                >
                  Change email / phone
                </Button>
                <Button
                  type="button"
                  size="small"
                  onClick={sendCode}
                  disabled={loading || resetDone || resendWait > 0}
                  sx={textButtonSx}
                >
                  {resendWait > 0 ? `Resend code in ${resendWait}s` : "Resend code"}
                </Button>
              </Box>

              {backToLogin}
            </Box>
          )}
        </Card>
      </Container>

      <AppToast open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};

export default ForgetPassword;
